import { ref, computed, onMounted, onUnmounted } from 'vue';

interface ResizableSidebarOptions {
  /** Storage key for localStorage */
  storageKey: string;
  /** Minimum width in pixels */
  minWidth?: number;
  /** Maximum width in pixels */
  maxWidth?: number;
  /** Default width in pixels */
  defaultWidth?: number;
}

/**
 * Composable for making a sidebar resizable with localStorage persistence
 * Also handles mobile sidebar toggle behavior
 * 
 * @param options - Configuration options for the resizable sidebar
 * @returns Object containing sidebar width ref, mobile state, and event handlers
 * 
 * @example
 * const { sidebarWidth, handleMouseDown, isMobile, toggleSidebar } = useResizableSidebar({
 *   storageKey: 'clientsSidebarWidth',
 *   minWidth: 200,
 *   maxWidth: 600,
 *   defaultWidth: 280
 * });
 */
export function useResizableSidebar(options: ResizableSidebarOptions) {
  const {
    storageKey,
    minWidth = 200,
    maxWidth = 800,
    defaultWidth = 280,
  } = options;

  const sidebarWidth = ref<number>(defaultWidth);
  const isResizing = ref(false);
  const startX = ref(0);
  const startWidth = ref(0);
  
  // Mobile state
  const isMobile = ref(false);
  const isMobileSidebarOpen = ref(false);

  /**
   * Load saved width from localStorage
   */
  const loadSavedWidth = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) {
          sidebarWidth.value = parsed;
        }
      }
    } catch (error) {
      console.warn(`Failed to load sidebar width from localStorage: ${error}`);
    }
  };

  /**
   * Save width to localStorage
   */
  const saveWidth = (width: number) => {
    try {
      localStorage.setItem(storageKey, width.toString());
    } catch (error) {
      console.warn(`Failed to save sidebar width to localStorage: ${error}`);
    }
  };

  /**
   * Check if we're on mobile
   */
  const checkMobile = () => {
    const wasMobile = isMobile.value;
    isMobile.value = window.innerWidth <= 768;
    
    // If transitioning from desktop to mobile, close sidebar
    if (!wasMobile && isMobile.value) {
      closeSidebar();
    }
  };

  /**
   * Open sidebar (mobile only)
   */
  const openSidebar = () => {
    if (isMobile.value) {
      isMobileSidebarOpen.value = true;
      document.body.classList.add('module-sidebar-open');
    }
  };

  /**
   * Close sidebar
   */
  const closeSidebar = () => {
    isMobileSidebarOpen.value = false;
    document.body.classList.remove('module-sidebar-open');
  };

  /**
   * Toggle sidebar
   */
  const toggleSidebar = () => {
    if (isMobileSidebarOpen.value) {
      closeSidebar();
    } else {
      openSidebar();
    }
  };

  /**
   * Computed class for sidebar
   */
  const sidebarClass = computed(() => {
    return {
      'sidebar-open': isMobile.value && isMobileSidebarOpen.value
    };
  });

  /**
   * Handle mouse move during resize
   */
  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing.value) return;

    const delta = e.clientX - startX.value;
    const newWidth = startWidth.value + delta;

    // Clamp width between min and max
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      sidebarWidth.value = newWidth;
    }
  };

  /**
   * Handle mouse up to finish resize
   */
  const handleMouseUp = () => {
    if (isResizing.value) {
      isResizing.value = false;
      saveWidth(sidebarWidth.value);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  };

  /**
   * Handle mouse down on resize handle
   */
  const handleMouseDown = (e: MouseEvent) => {
    isResizing.value = true;
    startX.value = e.clientX;
    startWidth.value = sidebarWidth.value;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  };

  // Setup event listeners
  onMounted(() => {
    loadSavedWidth();
    checkMobile();
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('resize', checkMobile);
  });

  // Cleanup event listeners
  onUnmounted(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    window.removeEventListener('resize', checkMobile);
    closeSidebar();
  });

  return {
    sidebarWidth,
    isResizing,
    handleMouseDown,
    // Mobile functionality
    isMobile,
    isMobileSidebarOpen,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    sidebarClass,
  };
}

